import {
  analyzeRedeemCsv,
  estimateRedeemCsv,
  startRedeemCsv,
  queryRedeemCsv,
  getPeopleContactTypes,
  getEnrichmentConstants,
} from "@/api/redeem";
// Poll cadence shared with EmailRedeemPopup.vue — see @/utils/redeemPolling.
import {
  FALLBACK_FLOOR_MS,
  pickInitialPollDelay,
  nextPollDelay,
} from "@/utils/redeemPolling";

const initialWizardState = () => ({
  step: 1,
  kind: "people",
  filename: null,
  csv_response: null,
  mapping_attributes: null,
  // Populated from /people/contacts/types via ensureContactTypes(); we avoid
  // hardcoding slugs here so the UI matches the backend exactly.
  people_contact_types: [],
  // v2: optional company-info enrichment. When true, backend resolves the
  // user's mapped domain column to a Pubrio company record and appends
  // company columns to the download. Charged via company_detail_credit.
  is_company_enrichment: false,
  notify_by_email: true,
  estimate: null,
  active_job: null,
});

const state = {
  ...initialWizardState(),
  // active poller handle so we can clear it on unmount / step change
  _poll_timer: null,
  _poll_delay: FALLBACK_FLOOR_MS,
  // Reference data fetched from the server. We avoid hardcoding the field
  // list and contact-type list on the frontend.
  contact_types: [], // [{ people_contact_type_id, slug, name }]
  contact_types_loaded: false,
  // Cached from /redeem/csv/constants. Loaded once per locale change.
  constants: null,
  constants_loaded: false,
};

const mutations = {
  setStep(state, step) {
    state.step = step;
  },
  setKind(state, kind) {
    state.kind = kind;
  },
  setFilename(state, name) {
    state.filename = name;
  },
  setCsvResponse(state, payload) {
    state.csv_response = payload;
  },
  setMappingAttributes(state, payload) {
    state.mapping_attributes = payload;
  },
  setPeopleContactTypes(state, payload) {
    state.people_contact_types = payload;
  },
  setNotifyByEmail(state, value) {
    state.notify_by_email = value;
  },
  setIsCompanyEnrichment(state, value) {
    state.is_company_enrichment = Boolean(value);
  },
  setEstimate(state, payload) {
    state.estimate = payload;
  },
  setActiveJob(state, payload) {
    // Skip if shallow signature unchanged so polling ticks don't trigger phantom re-renders.
    const prev = state.active_job;
    if (prev && payload &&
        prev.is_completed === payload.is_completed &&
        prev.is_processing === payload.is_processing &&
        prev.total_charged_credit === payload.total_charged_credit &&
        prev.metadata?.rows_processed === payload.metadata?.rows_processed &&
        prev.metadata?.rows_matched === payload.metadata?.rows_matched &&
        prev.metadata?.error_summary === payload.metadata?.error_summary) {
      return;
    }
    state.active_job = payload;
  },
  setPollTimer(state, timer) {
    state._poll_timer = timer;
  },
  setPollDelay(state, delay) {
    state._poll_delay = delay;
  },
  resetWizard(state) {
    Object.assign(state, initialWizardState());
    state._poll_delay = FALLBACK_FLOOR_MS;
  },
  setContactTypes(state, list) {
    state.contact_types = list;
    state.contact_types_loaded = true;
  },
  setConstants(state, payload) {
    state.constants = payload;
    state.constants_loaded = true;
  },
};

const actions = {
  async analyze({ commit }, formData) {
    const res = await analyzeRedeemCsv(formData);
    if (res && res.data) {
      commit("setCsvResponse", res.data);
    }
    return res;
  },

  async estimate({ state, commit, rootState }) {
    // Guard against firing before the wizard has the data the backend
    // requires. The Map step's watcher can race ahead of seedFromAutoDetect
    // on first mount and post an empty body, which the backend correctly
    // rejects with 400.
    const profile_id = rootState.user.currentId && rootState.user.currentId.profile_id;
    const mapping = state.mapping_attributes;
    const types = state.people_contact_types;
    // kind=company has no contact-types selection (output is CRM_Schema-driven);
    // skip the types-required guard so /estimate fires once the user maps a
    // company input slug. kind=people still needs at least one contact type.
    const types_required = state.kind !== "company";
    if (
      !profile_id ||
      !state.csv_response ||
      !state.csv_response.attachment_hash ||
      !mapping ||
      Object.keys(mapping).length === 0 ||
      (types_required && (!Array.isArray(types) || types.length === 0))
    ) {
      return null;
    }
    const res = await estimateRedeemCsv({
      profile_id,
      attachment_hash: state.csv_response.attachment_hash,
      mapping_attributes: mapping,
      kind: state.kind,
      people_contact_types: types,
      is_company_enrichment: state.is_company_enrichment === true,
    });
    if (res && res.data) {
      commit("setEstimate", res.data);
    }
    return res;
  },

  async start({ state, commit, dispatch, rootState }) {
    const profile_id = rootState.user.currentId && rootState.user.currentId.profile_id;
    const res = await startRedeemCsv({
      profile_id,
      attachment_hash: state.csv_response?.attachment_hash,
      mapping_attributes: state.mapping_attributes,
      kind: state.kind,
      people_contact_types: state.people_contact_types,
      is_company_enrichment: state.is_company_enrichment === true,
      notify_by_email: state.notify_by_email,
      filename: state.filename,
    });
    if (res && res.data) {
      commit("setActiveJob", res.data);
      dispatch("startPolling", res.data.redeem_query_id);
    }
    return res;
  },

  startPolling({ state, commit, dispatch, rootState }, redeem_query_id) {
    dispatch("stopPolling");
    const systemConfig = rootState.user && rootState.user.systemConfig;
    const initial = pickInitialPollDelay(
      state.active_job && state.active_job.estimated_time,
      state.active_job && state.active_job.estimated_time_type,
      systemConfig,
    );
    commit("setPollDelay", initial);
    const tick = async () => {
      if (document.hidden) {
        const t = setTimeout(tick, state._poll_delay);
        commit("setPollTimer", t);
        return;
      }
      try {
        const profile_id =
          rootState.user.currentId && rootState.user.currentId.profile_id;
        const res = await queryRedeemCsv({ profile_id, redeem_query_id });
        if (res && res.data) {
          commit("setActiveJob", res.data);
          if (res.data.is_completed) {
            dispatch("stopPolling");
            return;
          }
        }
      } catch (e) {
        // surface via interceptor; keep polling on transient errors
      }
      const next = nextPollDelay(state._poll_delay, systemConfig);
      commit("setPollDelay", next);
      const t = setTimeout(tick, next);
      commit("setPollTimer", t);
    };
    const t = setTimeout(tick, initial);
    commit("setPollTimer", t);
  },

  stopPolling({ state, commit }) {
    if (state._poll_timer) {
      clearTimeout(state._poll_timer);
      commit("setPollTimer", null);
    }
  },

  reset({ commit, dispatch }) {
    dispatch("stopPolling");
    commit("resetWizard");
  },

  // Fetch localized run_statuses + match_statuses + pubrio_column_prefix. Cached.
  async ensureConstants({ state, commit }) {
    if (state.constants_loaded) return state.constants;
    const res = await getEnrichmentConstants();
    const data = (res && res.data) || null;
    commit("setConstants", data);
    return data;
  },

  // Fetch the canonical contact-type list once per session. Cached.
  async ensureContactTypes({ state, commit }) {
    if (state.contact_types_loaded) return state.contact_types;
    const res = await getPeopleContactTypes();
    const list = (res && res.data) || [];
    commit("setContactTypes", list);
    // Default the user's selection to the first email-* type if nothing
    // is selected yet, so the wizard has something sensible on first mount.
    if (!state.people_contact_types || state.people_contact_types.length === 0) {
      const first_email = list.find((t) => /email/.test(t.slug || ""));
      if (first_email) {
        commit("setPeopleContactTypes", [first_email.slug]);
      }
    }
    return list;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
